/**
 * Progress Service - API client for progress tracking data
 *
 * Fetches aggregated progress data for students and curriculum outcomes.
 * Server returns sheet numbers only - names merged client-side from local SQLite.
 */

import { api } from './api-client';

// =============================================================================
// Types - Backend Response (snake_case)
// =============================================================================

interface StudentProgressResponse {
  sheet_number: string;
  class_id: string | null;
  total_assessments: number;
  total_score: number;
  max_score: number;
  avg_percentage: number;
  outcomes_assessed: string[];
  mastery_counts: {
    mastered: number;
    proficient: number;
    developing: number;
    needs_support: number;
  };
  recent_trend: 'improving' | 'declining' | 'stable' | 'insufficient_data';
  last_assessment_date: string | null;
}

interface OutcomeProgressResponse {
  outcome_id: string;
  subject: string;
  grade: string;
  outcome_text: string;
  students_assessed: number;
  total_assessments: number;
  avg_percentage: number | null;
  mastery_rate: number | null;
  mastery_counts: {
    mastered: number;
    proficient: number;
    developing: number;
    needs_support: number;
  };
}

interface StudentDetailResponse {
  sheet_number: string;
  class_id: string | null;
  total_assessments: number;
  avg_percentage: number;
  recent_trend: 'improving' | 'declining' | 'stable' | 'insufficient_data';
  outcomes: Array<{
    outcome_id: string;
    subject: string;
    outcome_text: string;
    assessments: number;
    avg_percentage: number;
    last_score: number | null;
    max_score: number | null;
    last_date: string | null;
  }>;
  recent_assessments: Array<{
    scan_id: string;
    worksheet_id: string;
    worksheet_title: string;
    percentage: number;
    total_score: number;
    max_score: number;
    created_at: string;
  }>;
}

interface OutcomeDetailResponse {
  outcome_id: string;
  subject: string;
  grade: string;
  outcome_text: string;
  students: Array<{
    sheet_number: string;
    class_id: string | null;
    assessments: number;
    avg_percentage: number;
    last_percentage: number | null;
    last_date: string | null;
  }>;
  worksheets: Array<{
    worksheet_id: string;
    worksheet_title: string;
    scan_count: number;
    avg_percentage: number | null;
  }>;
}

interface ClassStudentProgressResponse {
  items: StudentProgressResponse[];
  total: number;
  class_id: string;
}

interface ClassOutcomeProgressResponse {
  items: OutcomeProgressResponse[];
  total: number;
  class_id: string;
}

// =============================================================================
// Types - Frontend (camelCase)
// =============================================================================

export type TrendDirection = 'improving' | 'declining' | 'stable' | 'insufficient_data';

export interface MasteryCounts {
  mastered: number;
  proficient: number;
  developing: number;
  needsSupport: number;
}

export interface StudentProgress {
  sheetNumber: string;
  classId: string | null;
  totalAssessments: number;
  totalScore: number;
  maxScore: number;
  avgPercentage: number;
  outcomesAssessed: string[];
  masteryCounts: MasteryCounts;
  recentTrend: TrendDirection;
  lastAssessmentDate: string | null;
}

export interface OutcomeProgress {
  outcomeId: string;
  subject: string;
  grade: string;
  outcomeText: string;
  studentsAssessed: number;
  totalAssessments: number;
  avgPercentage: number | null;
  masteryRate: number | null;
  masteryCounts: MasteryCounts;
}

export interface OutcomeBreakdown {
  outcomeId: string;
  subject: string;
  outcomeText: string;
  assessments: number;
  avgPercentage: number;
  lastScore: number | null;
  maxScore: number | null;
  lastDate: string | null;
}

export interface RecentAssessment {
  scanId: string;
  worksheetId: string;
  worksheetTitle: string;
  percentage: number;
  totalScore: number;
  maxScore: number;
  createdAt: string;
}

export interface StudentDetail {
  sheetNumber: string;
  classId: string | null;
  totalAssessments: number;
  avgPercentage: number;
  recentTrend: TrendDirection;
  outcomes: OutcomeBreakdown[];
  recentAssessments: RecentAssessment[];
}

export interface OutcomeStudentBreakdown {
  sheetNumber: string;
  classId: string | null;
  assessments: number;
  avgPercentage: number;
  lastPercentage: number | null;
  lastDate: string | null;
}

export interface OutcomeWorksheet {
  worksheetId: string;
  worksheetTitle: string;
  scanCount: number;
  avgPercentage: number | null;
}

export interface OutcomeDetail {
  outcomeId: string;
  subject: string;
  grade: string;
  outcomeText: string;
  students: OutcomeStudentBreakdown[];
  worksheets: OutcomeWorksheet[];
}

export interface ClassStudentProgressResult {
  items: StudentProgress[];
  total: number;
  classId: string;
}

export interface ClassOutcomeProgressResult {
  items: OutcomeProgress[];
  total: number;
  classId: string;
}

// =============================================================================
// Transformers
// =============================================================================

function transformMasteryCounts(data: { mastered: number; proficient: number; developing: number; needs_support: number }): MasteryCounts {
  return {
    mastered: data.mastered,
    proficient: data.proficient,
    developing: data.developing,
    needsSupport: data.needs_support,
  };
}

function transformStudentProgress(data: StudentProgressResponse): StudentProgress {
  return {
    sheetNumber: data.sheet_number,
    classId: data.class_id,
    totalAssessments: data.total_assessments,
    totalScore: data.total_score,
    maxScore: data.max_score,
    avgPercentage: data.avg_percentage,
    outcomesAssessed: data.outcomes_assessed,
    masteryCounts: transformMasteryCounts(data.mastery_counts),
    recentTrend: data.recent_trend,
    lastAssessmentDate: data.last_assessment_date,
  };
}

function transformOutcomeProgress(data: OutcomeProgressResponse): OutcomeProgress {
  return {
    outcomeId: data.outcome_id,
    subject: data.subject,
    grade: data.grade,
    outcomeText: data.outcome_text,
    studentsAssessed: data.students_assessed,
    totalAssessments: data.total_assessments,
    avgPercentage: data.avg_percentage,
    masteryRate: data.mastery_rate,
    masteryCounts: transformMasteryCounts(data.mastery_counts),
  };
}

function transformStudentDetail(data: StudentDetailResponse): StudentDetail {
  return {
    sheetNumber: data.sheet_number,
    classId: data.class_id,
    totalAssessments: data.total_assessments,
    avgPercentage: data.avg_percentage,
    recentTrend: data.recent_trend,
    outcomes: data.outcomes.map((o) => ({
      outcomeId: o.outcome_id,
      subject: o.subject,
      outcomeText: o.outcome_text,
      assessments: o.assessments,
      avgPercentage: o.avg_percentage,
      lastScore: o.last_score,
      maxScore: o.max_score,
      lastDate: o.last_date,
    })),
    recentAssessments: data.recent_assessments.map((a) => ({
      scanId: a.scan_id,
      worksheetId: a.worksheet_id,
      worksheetTitle: a.worksheet_title,
      percentage: a.percentage,
      totalScore: a.total_score,
      maxScore: a.max_score,
      createdAt: a.created_at,
    })),
  };
}

function transformOutcomeDetail(data: OutcomeDetailResponse): OutcomeDetail {
  return {
    outcomeId: data.outcome_id,
    subject: data.subject,
    grade: data.grade,
    outcomeText: data.outcome_text,
    students: data.students.map((s) => ({
      sheetNumber: s.sheet_number,
      classId: s.class_id,
      assessments: s.assessments,
      avgPercentage: s.avg_percentage,
      lastPercentage: s.last_percentage,
      lastDate: s.last_date,
    })),
    worksheets: data.worksheets.map((w) => ({
      worksheetId: w.worksheet_id,
      worksheetTitle: w.worksheet_title,
      scanCount: w.scan_count,
      avgPercentage: w.avg_percentage,
    })),
  };
}

// =============================================================================
// Service Class
// =============================================================================

class ProgressService {
  /**
   * Get student progress data for a class.
   * Returns aggregated progress for each student (by sheet number).
   */
  async getClassStudentProgress(
    classId: string,
    signal?: AbortSignal
  ): Promise<ClassStudentProgressResult> {
    const response = await api.get<ClassStudentProgressResponse>(
      `/progress/students/${encodeURIComponent(classId)}`,
      { signal }
    );

    return {
      items: response.items.map(transformStudentProgress),
      total: response.total,
      classId: response.class_id,
    };
  }

  /**
   * Get outcome progress data for a class.
   * Returns aggregated progress for each curriculum outcome.
   */
  async getClassOutcomeProgress(
    classId: string,
    signal?: AbortSignal
  ): Promise<ClassOutcomeProgressResult> {
    const response = await api.get<ClassOutcomeProgressResponse>(
      `/progress/outcomes/${encodeURIComponent(classId)}`,
      { signal }
    );

    return {
      items: response.items.map(transformOutcomeProgress),
      total: response.total,
      classId: response.class_id,
    };
  }

  /**
   * Get detailed progress data for a specific student.
   * Includes outcome breakdowns and recent assessments.
   */
  async getStudentDetail(
    sheetNumber: string,
    classId?: string,
    signal?: AbortSignal
  ): Promise<StudentDetail> {
    const params = new URLSearchParams();
    if (classId) {
      params.set('class_id', classId);
    }

    const url = `/progress/student/${encodeURIComponent(sheetNumber)}${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get<StudentDetailResponse>(url, { signal });

    return transformStudentDetail(response);
  }

  /**
   * Get detailed progress data for a specific outcome.
   * Includes per-student breakdown and worksheets.
   */
  async getOutcomeDetail(
    outcomeId: string,
    classId?: string,
    signal?: AbortSignal
  ): Promise<OutcomeDetail> {
    const params = new URLSearchParams();
    if (classId) {
      params.set('class_id', classId);
    }

    const url = `/progress/outcome/${encodeURIComponent(outcomeId)}${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get<OutcomeDetailResponse>(url, { signal });

    return transformOutcomeDetail(response);
  }
}

// Export singleton instance
export const progressService = new ProgressService();

/**
 * Check if an error is a request cancellation.
 */
export function isRequestCancelled(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }
  // Axios cancellation
  if (error && typeof error === 'object' && 'code' in error) {
    return (error as { code: string }).code === 'ERR_CANCELED';
  }
  return false;
}
